/**
 * Amplitude Browser SDK 2 initialisation.
 *
 * This is the JS Browser SDK — it runs in the Capacitor hosted WebView on
 * iOS/Android exactly as it does on web/PWA, so there is no native SDK and
 * no native permission/manifest change (see docs/amplitude-analytics-plan.md
 * §6 / §6b).
 *
 * Init is:
 *   - idempotent (safe to call repeatedly)
 *   - client-only (no-ops during SSR)
 *   - opt-out aware (Do-Not-Track + local opt-out flag)
 *   - disabled when no API key is set (dev convenience — the whole pipeline
 *     no-ops cleanly with no key, so nothing leaks before a key exists)
 *
 * Data residency is pinned to the EU — the API key MUST belong to an EU
 * Amplitude project or events are rejected. This is a one-way door we chose
 * deliberately for the UK/GDPR posture.
 */
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { ANALYTICS_OPTOUT_KEY, hasOptedOut } from './opt-out';

// Re-exported so existing imports keep working; the definitions live in
// opt-out.ts, which the screen-time tracker reads without loading this SDK.
export { ANALYTICS_OPTOUT_KEY, hasOptedOut };

/**
 * Fraction of sessions to record for replay (0–1). 100% while in beta — at
 * current traffic this stays under Amplitude's free 1,000-replays/month
 * quota, and every session is informative early on. Dial down as we grow.
 */
const SESSION_REPLAY_SAMPLE_RATE = 1;

let ready = false;

/** True once the SDK is initialised and sending. `track()`/`identify()` no-op until then. */
export function isAmplitudeReady(): boolean {
  return ready;
}

/**
 * Turn analytics + session recording off (or back on) for this user, on this
 * device. Backs the toggle in Settings → Account, which the privacy policy
 * points at ("you can opt out at any time in Settings") — so this is a promise
 * we've made publicly, not a nice-to-have.
 *
 * Opting out calls Amplitude's own `setOptOut`, which stops events AND session
 * recording immediately in the live SDK — no reload needed. The localStorage
 * flag is what makes it stick across launches (checked by `hasOptedOut()`
 * before the SDK is even initialised, so an opted-out user never starts a
 * recording in the first place).
 */
export function setAnalyticsOptOut(optOut: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (optOut) {
      window.localStorage.setItem(ANALYTICS_OPTOUT_KEY, '1');
    } else {
      window.localStorage.removeItem(ANALYTICS_OPTOUT_KEY);
    }
  } catch {
    // private-mode localStorage — the in-session opt-out below still applies.
  }
  // Only meaningful if the SDK was initialised this session (i.e. they were
  // opted in at load). Opting back in mid-session doesn't retro-start a
  // recording; it takes effect on next launch.
  if (ready) amplitude.setOptOut(optOut);
}

/**
 * Enrichment plugin that stamps `platform` (web | pwa | ios | android) onto
 * every event. Cleaner than a user property because the same user can be on
 * web today and the iOS app tomorrow — we want per-event accuracy, not
 * last-write-wins.
 */
function platformPlugin(platform: string): amplitude.Types.EnrichmentPlugin {
  return {
    name: 'mucka-platform-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      event.event_properties = { platform, ...event.event_properties };
      return event;
    },
  };
}

export function initAmplitude(opts: { platform: string }): void {
  if (ready) return;
  if (typeof window === 'undefined') return; // SSR guard
  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  if (!apiKey) return; // analytics disabled (no key) — clean no-op
  if (hasOptedOut()) return;

  amplitude.add(platformPlugin(opts.platform));

  // Session Replay — gated behind its OWN flag (default OFF), SEPARATE from the
  // analytics key. Analytics is already live in prod on the shared key; this
  // flag is what stops 100%-sample recording from riding along before the
  // privacy-policy + store-disclosure approvals (App Store labels / Play Data
  // Safety) are in place. Flip NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY=1 in Vercel
  // and redeploy to turn recording on; until then it stays inert.
  //
  // Conservative privacy: masks ALL text + inputs, so recordings never capture
  // client names, addresses, job notes or money — just layout, clicks, scrolls
  // and navigation. Inherits the EU server zone from the init config below.
  if (process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY === '1') {
    amplitude.add(
      sessionReplayPlugin({
        sampleRate: SESSION_REPLAY_SAMPLE_RATE,
        privacyConfig: { defaultMaskLevel: 'conservative' },
      }),
    );
  }

  amplitude.init(apiKey, {
    serverZone: 'EU',
    // Autocapture: we take the BREADTH signals (page views, sessions,
    // marketing attribution) but deliberately switch OFF the ones that
    // scrape arbitrary on-screen text / form values — those can hoover up
    // client names, addresses and money into event props, which our PII
    // rule forbids. Interaction-level events come from the curated taxonomy
    // instead. See docs/amplitude-analytics-plan.md §3.
    autocapture: {
      pageViews: true,
      sessions: true,
      attribution: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
  });

  ready = true;
}
